import { computed, Injectable, signal } from '@angular/core';
import { Params } from '@angular/router';
import { environment } from '../../environments/environment';

/**
 * Ein Event aus der Mediathek, wie es die Antwort des Mediathek-Logins liefert.
 *
 * Die Titel kommen als eigenes Feld pro Sprache (title_de, title_en, …), `lang` sagt,
 * in welchen Sprachen das Event überhaupt angeboten wird.
 */
export interface MediaLibraryEvent {
  token: string;
  lang: string[];
  [titleByLang: string]: any;
}

/**
 * Hält den Mediathek-Kontext eines Aufrufs: den Token, den Rücksprung-Link und die
 * Liste der weiteren Events.
 *
 * Ein eigener Store, weil drei Stellen beteiligt sind: die EventShellComponent liest den
 * Kontext aus der URL (sie läuft bei jedem Aufruf, auch wenn der Login übersprungen
 * wird), die LoginComponent liefert die Event-Liste aus der Login-Antwort nach, und der
 * Header zeigt beides an.
 */
@Injectable({
  providedIn: 'root'
})
export class MediaLibraryStoreService {

  private readonly _mToken = signal('');
  private readonly _backLinkEncoded = signal('');
  private readonly _events = signal<MediaLibraryEvent[]>([]);
  private readonly _tracker = signal<{ code?: string; cookies?: string }>({});
  private readonly _panelOpen = signal(false);

  /** Mediathek-Token, URL-kodiert — so wird er an Backend und Folge-Events weitergegeben. */
  readonly mToken = this._mToken.asReadonly();

  /** Rücksprung-Link, base64-kodiert wie im Query-Parameter `back`. */
  readonly backLinkEncoded = this._backLinkEncoded.asReadonly();

  readonly events = this._events.asReadonly();

  /** Ist das Event-Menü (Hamburger) offen? Der Header braucht dafür Platz. */
  readonly panelOpen = this._panelOpen.asReadonly();

  /** Rücksprung-Link im Klartext, für href und für den Fallback bei fehlgeschlagenem Login. */
  readonly backLink = computed(() => {
    const encoded = this._backLinkEncoded();
    if (!encoded) return '';
    try {
      return decodeURI(atob(encoded));
    } catch {
      return '';
    }
  });

  /**
   * Kommt der Aufruf aus der Mediathek? Nur dann gehören Rücksprung-Button und
   * Hamburger überhaupt in den Header.
   */
  readonly isFromMediaLibrary = computed(() => !!this._mToken());

  /**
   * Übernimmt den Mediathek-Kontext aus den Query-Parametern.
   *
   * Ohne `medialib` bleibt der Store leer — dann kam der Aufruf nicht aus der Mediathek
   * und der Header bleibt unverändert.
   */
  captureFromQueryParams(qp: Params): void {
    if (!qp['medialib']) return;

    this._mToken.set(encodeURIComponent(qp['medialib']));
    this._backLinkEncoded.set(qp['back'] ?? '');
    this._tracker.set({ code: qp['eTracker'], cookies: qp['eTrackerCookies'] });
  }

  setEvents(events: MediaLibraryEvent[] | undefined | null): void {
    this._events.set(Array.isArray(events) ? events : []);
  }

  togglePanel(): void {
    this._panelOpen.set(!this._panelOpen());
  }

  closePanel(): void {
    this._panelOpen.set(false);
  }

  /**
   * Die Events, die in der aktiven Sprache laufen. Events ohne diese Sprache hätten im
   * Menü nur einen leeren Titel.
   */
  eventsForLang(lang: string): MediaLibraryEvent[] {
    return this._events().filter(event => event.lang?.includes(lang));
  }

  titleFor(event: MediaLibraryEvent, lang: string): string {
    return event['title_' + lang] ?? '';
  }

  /**
   * Link auf ein anderes Mediathek-Event.
   *
   * Zeigt bewusst auf die ausgelieferte Anwendung (environment.apiUrl), nicht auf den
   * aktuellen Host: der Mediathek-Token gilt für diese Installation. Token und
   * Rücksprung-Link werden mitgegeben, damit auch das nächste Event den Login
   * überspringt und den Weg zurück kennt.
   */
  eventUrl(token: string, lang: string): string {
    const tracker = this._tracker();
    const params = [
      `ln=${lang}`,
      `medialib=${this._mToken()}`,
      `back=${this._backLinkEncoded()}`
    ];

    if (tracker.code) {
      params.push(`eTracker=${encodeURIComponent(tracker.code)}`);
      params.push(`eTrackerCookies=${encodeURIComponent(tracker.cookies ?? '')}`);
    }

    return `${environment.apiUrl}${environment.eventPrefix}${token}?${params.join('&')}`;
  }
}
